import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Plus, Minus } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import { faqSchema, breadcrumbSchema } from '../lib/schema';

const FAQS = [
  { q:'Is a MODULAROOM a prefab building?', a:'No. A MODULAROOM is engineered and delivered as a complete finished product. The structural frame, envelope, interior finishes, MEP and glazing are integrated at the factory. On site, we only place, level and connect utilities.' },
  { q:'How long does production take?', a:'Approximately 90 to 120 days from order confirmation to dispatch. Site placement and utility connection add a further 1 to 3 days.' },
  { q:'Do you deliver internationally?', a:'Yes. MODULAROOM is manufactured in India and delivered worldwide via road and sea logistics tailored to each destination.' },
  { q:'What foundation is required?', a:'A level, load-rated pad prepared to our specifications. This is the only significant civil work required at your site.' },
  { q:'Can I customise the interior?', a:'Exterior paint tones, interior wood species, stone, textiles and select fittings can be tailored to your project. Our team collaborates with your architect on the specification.' },
  { q:'What is the warranty?', a:'Product-grade warranty: 10 years structural, 5 years envelope, 2 years MEP. See our Engineering page for the full assurance framework and future compliance roadmap.' },
  { q:'Are units energy-efficient?', a:'Yes. High-density PU insulation, low-E double glazing and a continuous vapour seal produce excellent thermal performance across climates.' },
  { q:'Do you supply units for resort operators?', a:'Yes. Many of our clients are luxury resort, boutique hotel and glamping operators. See our Hospitality page for typical use cases.' },
  { q:'What sizes are available?', a:'Seven models between 19 sq m and 37 sq m of interior carpet area. See the Collections page for the full range.' },
  { q:'How do I begin a project?', a:'Book a consultation. Our team will review your site, purpose and timeline, and return with a curated proposal, usually within one business day.' },
];

function Item({ q, a, open, onToggle }) {
  return (
    <div className="border-t border-black/10">
      <button onClick={onToggle} className="w-full py-6 flex items-start justify-between gap-6 text-left">
        <div className="font-display text-xl md:text-2xl tracking-tight max-w-2xl">{q}</div>
        <div className="mt-1 text-black/50 shrink-0">{open ? <Minus size={18}/> : <Plus size={18}/>}</div>
      </button>
      <div className={`overflow-hidden transition-all duration-500 ${open ? 'max-h-96 pb-8' : 'max-h-0'}`}>
        <div className="max-w-2xl text-black/65 leading-relaxed">{a}</div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <main data-testid="faq-page" className="bg-white text-[#111]">
      <Helmet>
        <title>FAQ | MODULAROOM</title>
        <meta name="description" content="Answers about luxury modular homes, prefab cottages, delivery timelines, warranty, foundations, customisation and international shipping from MODULAROOM." />
        <link rel="canonical" href="https://modularoom.com/faq" />
        <script type="application/ld+json">{JSON.stringify(faqSchema(FAQS))}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Support', url: '/faq' },
            { name: 'FAQ', url: '/faq' },
          ]))}
        </script>
      </Helmet>
      <section className="pt-40 md:pt-56 pb-16 max-w-[1440px] mx-auto px-6 md:px-12">
        <Breadcrumbs items={[{label:'Home', to:'/'}, {label:'Support'}, {label:'FAQ'}]} />
        <h1 className="mt-14 font-display text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-4xl">
          Questions,<br/><span className="italic text-black/40">answered.</span>
        </h1>
      </section>

      <section className="pb-32 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          {FAQS.map((f, i) => (
            <Item key={f.q} q={f.q} a={f.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
          <div className="border-t border-black/10 mt-2 pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="text-black/60">Something not covered here?</div>
            <Link to="/contact#book" className="inline-flex items-center gap-2 rounded-full bg-[#111] text-white px-6 py-3 text-[12px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] transition-colors">Contact our team</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
